import React from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CssBaseline,
  Alert,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';

const EDC_ACTIVATION_SESSION_KEY = 'edc_activation';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  width: '100vw',
  backgroundColor: '#F9FAFB',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'fixed',
  top: 0,
  left: 0,
});

const LogoWrapper = styled(Box)({
  position: 'absolute',
  top: '-50px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100px',
  height: '100px',
  '& .logo-content': {
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: '50%',
    padding: '4px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    animation: `${fadeInUp} 0.8s ease-out`,
  },
  '& .logo': {
    width: '100%',
    height: '100%',
    backgroundImage: 'url("/images/edc_logo_circle.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
});

const StyledCard = styled(Paper)({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
  maxWidth: '520px',
  width: '100%',
  padding: '40px',
  paddingTop: '80px',
  textAlign: 'center',
  position: 'relative',
  marginTop: '60px',
});

const CTAButton = styled(Button)({
  backgroundColor: '#4F46E5',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: '32px',
  textTransform: 'none',
  boxShadow: '0px 4px 6px rgba(79, 70, 229, 0.3)',
  marginTop: '24px',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: '0px 4px 8px rgba(79, 70, 229, 0.4)',
  },
});

export default function AccountActivation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'invite';
  const isReset = type === 'reset';

  if (!token) {
    return (
      <PageWrapper>
        <CssBaseline />
        <Container maxWidth="sm">
          <StyledCard elevation={0}>
            <LogoWrapper>
              <div className="logo-content">
                <div className="logo" aria-label="EDC Logo" />
              </div>
            </LogoWrapper>
            <Alert severity="error" sx={{ mb: 3 }}>
              This link is missing required information. Please use the link
              from your email exactly as received.
            </Alert>
            <Button component={Link} to="/login" variant="outlined">
              Return to Login
            </Button>
          </StyledCard>
        </Container>
      </PageWrapper>
    );
  }

  const handleActivate = () => {
    // Store token in sessionStorage so PasswordSetup can read it
    // without exposing it in the URL where scanners could act on it.
    sessionStorage.setItem(
      EDC_ACTIVATION_SESSION_KEY,
      JSON.stringify({ token, type })
    );
    navigate('/set-password');
  };

  return (
    <PageWrapper>
      <CssBaseline />
      <Container maxWidth="sm">
        <StyledCard elevation={0}>
          <LogoWrapper>
            <div className="logo-content">
              <div className="logo" aria-label="EDC Logo" />
            </div>
          </LogoWrapper>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              color: '#4F46E5',
            }}
          >
            {isReset ? (
              <LockResetOutlinedIcon sx={{ fontSize: 48 }} />
            ) : (
              <LockOpenOutlinedIcon sx={{ fontSize: 48 }} />
            )}
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: '#111827', mb: 2 }}
          >
            {isReset ? 'Reset Your Password' : 'Welcome to School Explorer'}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: '#6B7280', mb: 1, lineHeight: 1.6 }}
          >
            {isReset
              ? "Click the button below to securely reset your password. You'll be prompted to choose a new one."
              : "Your account is ready. Click the button below to securely set up your password and get started."}
          </Typography>

          <CTAButton fullWidth onClick={handleActivate}>
            {isReset ? 'Reset My Password' : 'Set Up My Account'}
          </CTAButton>

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Having trouble?{' '}
              <Link to="/contact" style={{ color: '#4F46E5', textDecoration: 'none' }}>
                Contact support
              </Link>
              {' '}or{' '}
              <Link to="/login" style={{ color: '#4F46E5', textDecoration: 'none' }}>
                return to login
              </Link>
              .
            </Typography>
          </Box>
        </StyledCard>
      </Container>
    </PageWrapper>
  );
}
